/**
 * Les pièces à préparer d'une intervention et la fiche de préparation d'un jour (lot PL4, ADR-0015 ; ADR-0016) : la logique PURE de l'écran.
 *
 * Rien n'est saisi : les pièces viennent de la gamme d'entretien de la machine, au palier choisi pour l'intervention, lues par le
 * serveur (sans prix). L'écran choisit le palier (en proposant celui des heures, sans jamais le choisir à la place de
 * l'utilisateur), montre les pièces, et imprime la fiche du jour avec son total additionné. Aucun prix, aucun stock : on LISTE
 * ce qu'il faut sortir, on ne dit pas si c'est au magasin.
 *
 * Une quantité inconnue reste inconnue (« à préciser »), jamais « 1 » : c'est la règle de la gamme, elle vaut ici.
 */
import type {
  EtatPieces,
  MaintenanceMachine,
  PieceTotalVisite,
  PieceVisite,
  PiecesMachineVisite,
  Preparation,
  ProchainEntretien,
  VisiteMachine,
} from '../api/client';
import { paliersDeGamme, type PalierDeGamme } from './bon-gamme';
import { jourLong, jourValide, libelleMachine } from './planning';

// --- Dire ce qu'on voit ----------------------------------------------------------------------------

/** Ce que dit l'écran quand une machine n'a pas de pièces à montrer : toujours POURQUOI, et quoi faire. */
export const PHRASE_ETAT_PIECES: Readonly<Record<EtatPieces, string>> = {
  OK: '',
  SANS_GAMME: 'Aucune gamme n’est rattachée à cette machine : rien à préparer automatiquement.',
  GAMME_SANS_VERSION: 'La gamme de cette machine n’a aucune version en vigueur à la date de la visite : rien à préparer.',
  PALIER_A_CHOISIR: 'Choisissez le palier pour voir les pièces à préparer.',
  PALIER_INCONNU: 'Le palier choisi n’existe plus dans la gamme : choisissez-en un autre.',
  AUCUNE_PIECE: 'Ce palier n’a aucune pièce à remplacer.',
};

/** « 14 », « 2,5 » : le décimal du serveur en texte à la française, sans zéros inutiles. */
export function formaterQuantite(texte: string): string {
  const t = texte.trim();
  if (!t.includes('.')) return t;
  return t.replace(/0+$/, '').replace(/\.$/, '').replace('.', ',');
}

/**
 * La quantité d'une pièce : « 14 », « 14 l », « à préciser » quand la gamme ne la connaît pas (jamais « 1 »). Pour un total,
 * `incomplete` ajoute ce qui reste à préciser : « 3 (+ à préciser) », le total n'est alors qu'un minimum.
 */
export function quantiteAffichee(quantite: string | null, unite: string | null, incomplete = false): string {
  if (quantite === null) return 'à préciser';
  const avecUnite = `${formaterQuantite(quantite)}${unite && unite.trim() !== '' ? ` ${unite.trim()}` : ''}`;
  return incomplete ? `${avecUnite} (+ à préciser)` : avecUnite;
}

/** « 9 pièces », « 1 pièce », « aucune pièce ». */
export function phrasePieces(n: number): string {
  if (n === 0) return 'aucune pièce';
  return n === 1 ? '1 pièce' : `${n} pièces`;
}

// --- Choisir le palier ------------------------------------------------------------------------------

const NOMBRE = new Intl.NumberFormat('fr-FR');

/**
 * Le palier à PROPOSER d'après le prochain entretien de la machine : celui du point en retard s'il y en a un, sinon celui du
 * prochain point. Jamais choisi à la place de l'utilisateur : l'écran l'affiche comme une proposition et le laisse choisir.
 * Rien à proposer quand le calcul n'a rien à dire (aucun compteur, aucun relevé…) ou quand le code ne se retrouve pas dans les
 * paliers de la gamme.
 */
export function propositionDePalier(
  prochain: ProchainEntretien | null,
  paliers: readonly Pick<PalierDeGamme, 'levelKey' | 'code'>[],
): { readonly levelKey: string; readonly code: string; readonly raison: string } | null {
  if (prochain === null || prochain.etat === 'A_RENSEIGNER') return null;
  const point = prochain.enRetard ?? prochain.prochain;
  if (point === null) return null;
  const palier = paliers.find((p) => p.code.trim().toLowerCase() === point.code.trim().toLowerCase());
  if (!palier) return null;

  const releve = prochain.valeur ? ` (${NOMBRE.format(Number(prochain.valeur.value))} h relevées)` : '';
  const raison = prochain.enRetard
    ? `palier ${palier.code} en retard, dépassé de ${NOMBRE.format(prochain.enRetard.depassement)} h${releve}`
    : `palier ${palier.code}, prochain point à ${NOMBRE.format(prochain.prochain?.cible ?? 0)} h${releve}`;
  return { levelKey: palier.levelKey, code: palier.code, raison };
}

/** Ce que le volet sait d'une machine choisie : sa gamme, ses paliers (avec le nombre de pièces), la proposition des heures. */
export interface InfoPaliersMachine {
  readonly gamme: string | null;
  readonly paliers: readonly PalierDeGamme[];
  readonly proposition: ReturnType<typeof propositionDePalier>;
}

export function infoPaliersMachine(maintenance: MaintenanceMachine | null, prochain: ProchainEntretien | null): InfoPaliersMachine {
  const paliers = paliersDeGamme(maintenance?.rattachement?.version?.contenu);
  return {
    gamme: maintenance?.rattachement?.profile.label ?? null,
    paliers,
    proposition: propositionDePalier(prochain, paliers),
  };
}

// --- La fiche de préparation -----------------------------------------------------------------------

/** Le lien de la fiche d'un jour (et d'un technicien, ou de tous). */
export function lienFiche(jour: string, technicienId?: string | null): string {
  const p = new URLSearchParams({ jour });
  if (technicienId) p.set('technicien', technicienId);
  return `/planning/preparation?${p.toString()}`;
}

/** Lit l'adresse de la fiche : un jour mal écrit est ignoré (jamais une page cassée par un lien tronqué). */
export function lireParametresFiche(params: URLSearchParams): { readonly jour: string | null; readonly technicien: string | null } {
  const jour = params.get('jour');
  const technicien = params.get('technicien');
  return { jour: jour !== null && jourValide(jour) ? jour : null, technicien: technicien === null || technicien === '' ? null : technicien };
}

/** « mercredi 21 octobre 2026 ». */
export function jourComplet(jour: string): string {
  return `${jourLong(jour)} ${jour.slice(0, 4)}`;
}

/** Le nom de la fiche — celui que le navigateur propose au PDF : « Fiche de préparation – mercredi 21 octobre 2026 – Gregory BONFILS ». */
export function nomFiche(p: Pick<Preparation, 'jour' | 'technicien'>): string {
  return `Fiche de préparation – ${jourComplet(p.jour)} – ${p.technicien?.name ?? 'tous les techniciens'}`;
}

/** « 2 visites », « 1 visite », « aucune visite ». */
export function phraseVisites(n: number): string {
  if (n === 0) return 'aucune visite';
  return n === 1 ? '1 visite' : `${n} visites`;
}

/** La ligne sous le titre de la fiche : le jour, le technicien (ou « tous les techniciens »), le nombre de visites. */
export function sousTitreFiche(p: Pick<Preparation, 'jour' | 'technicien' | 'visites'>): string {
  const jour = jourComplet(p.jour);
  return `${jour.charAt(0).toUpperCase()}${jour.slice(1)} — ${p.technicien?.name ?? 'tous les techniciens'} — ${phraseVisites(p.visites.length)}`;
}

/** « visite 1 », « visites 1 et 2 », « visites 1, 2 et 3 » : d'où vient une ligne du total. */
export function phrasePour(visites: readonly number[]): string {
  if (visites.length === 1) return `visite ${visites[0]}`;
  const debut = visites.slice(0, -1).join(', ');
  return `visites ${debut} et ${visites[visites.length - 1]}`;
}

/** Une ligne du total, prête à s'imprimer. */
export function ligneDuTotal(t: PieceTotalVisite): { readonly reference: string; readonly designation: string; readonly quantite: string; readonly pour: string } {
  return {
    reference: t.reference ?? '—',
    designation: t.designation,
    quantite: quantiteAffichee(t.quantite, t.unite, t.quantiteIncomplete),
    pour: phrasePour(t.visites),
  };
}

/**
 * Ce que la fiche signale EN HAUT, pour qu'on ne croie pas une visite complète quand elle ne l'est pas : une machine sans
 * gamme, sans palier choisi, au palier inconnu ; une visite posée sur un site sans machine ; les visites d'atelier laissées de côté.
 */
export function signalementsFiche(p: Pick<Preparation, 'visites' | 'visitesAtelier'>): string[] {
  const dits: string[] = [];
  for (const v of p.visites) {
    if (v.machines.length === 0) {
      dits.push(`Visite ${v.ordre} (${v.client}) : aucune machine indiquée — aucune pièce listée.`);
      continue;
    }
    for (const { machine, pieces } of v.machines) {
      if (pieces.etat === 'OK') continue;
      dits.push(`Visite ${v.ordre} (${libelleMachine(machine)}) : ${PHRASE_ETAT_PIECES[pieces.etat].replace(/\.$/, '')} — aucune pièce listée.`);
    }
  }
  if (p.visitesAtelier > 0) {
    dits.push(
      `${p.visitesAtelier === 1 ? '1 visite d’atelier ne figure' : `${p.visitesAtelier} visites d’atelier ne figurent`} pas sur la fiche : elle${p.visitesAtelier === 1 ? ' n’a' : 's n’ont'} pas de machine, donc pas de pièces.`,
    );
  }
  return dits;
}

/** La machine d'une visite sur la fiche : « KAESER-ESD442SFC/1312-ESD.3 · palier B ». */
export function enteteMachineFiche(machine: VisiteMachine, pieces: PiecesMachineVisite): string {
  return pieces.palier ? `${libelleMachine(machine)} · palier ${pieces.palier.code}` : libelleMachine(machine);
}

/**
 * Les pièces à montrer et à imprimer pour une machine : celles du palier, seulement quand il est choisi (jamais les pièces
 * d'un palier qui ne l'est pas). Les pièces demandées en plus sont des lignes de l'intervention : elles arrivent déjà dans
 * `pieces` (une seule source, ADR-0016).
 */
export function lignesDeLaMachine(p: Pick<PiecesMachineVisite, 'etat' | 'pieces'>): readonly PieceVisite[] {
  return p.etat === 'OK' ? p.pieces : [];
}
